import { useEffect, useRef } from "react";

import type { ReplayRailNode, ReplaySide } from "@exploitlens/ui-contracts";

import { formatWei } from "../../lib/format";
import { prefersReducedMotion } from "../../lib/motion";
import { useSelection } from "../../context/SelectionContext";
import { RelationBadge } from "./RelationBadge";

const RESULT_SYMBOL: Record<string, string> = { return: "→", revert: "↩", halt: "⛔" };

/**
 * A deep reentrancy loop can recurse dozens of levels -- real fixture data
 * hits 24+ on this bundle's vulnerable side. Indenting every level would run
 * rows off the right edge of a judge-laptop viewport, so indentation is
 * capped and the true depth is shown as a chip once it's clamped.
 */
const MAX_INDENT_DEPTH = 6;

export function AlignedCallRow({
  node,
  side,
  depth,
  hasChildren,
  collapsed,
  onToggleCollapse,
}: {
  node: ReplayRailNode;
  side: ReplaySide;
  depth: number;
  hasChildren: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { selection, select } = useSelection();
  const ref = useRef<HTMLDivElement>(null);

  const isSelected =
    (selection?.side === side && selection.nodeId === node.id) ||
    (selection?.side !== side && selection?.counterpartId === node.id);

  useEffect(() => {
    // jsdom (component tests) has no `scrollIntoView` implementation at all.
    if (isSelected && typeof ref.current?.scrollIntoView === "function") {
      ref.current.scrollIntoView({
        block: "nearest",
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    }
  }, [isSelected]);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={() => select({ side, nodeId: node.id, counterpartId: node.counterpartId })}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          select({ side, nodeId: node.id, counterpartId: node.counterpartId });
        }
      }}
      style={{ paddingInlineStart: `${Math.min(depth, MAX_INDENT_DEPTH) * 1.25}rem` }}
      className={[
        "flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 text-sm",
        isSelected ? "border-accent bg-canvas-raised" : "border-transparent hover:bg-canvas-raised",
      ].join(" ")}
    >
      {hasChildren ? (
        <button
          type="button"
          aria-label={collapsed ? `Expand ${node.label}` : `Collapse ${node.label}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleCollapse();
          }}
          className="w-4 shrink-0 text-ink-faint"
        >
          {collapsed ? "▸" : "▾"}
        </button>
      ) : (
        <span className="w-4 shrink-0" aria-hidden="true" />
      )}

      {depth > MAX_INDENT_DEPTH && (
        <span
          className="shrink-0 rounded border border-rule px-1 text-[10px] text-ink-faint"
          title={`Nesting depth ${depth} (indentation capped at ${MAX_INDENT_DEPTH})`}
        >
          ⌊{depth}
        </span>
      )}

      <span className="min-w-0 flex-1 truncate font-evidence text-ink" title={node.semanticPath}>
        {node.label}
      </span>

      <span className="shrink-0 text-ink-faint" aria-label={`Result: ${node.resultClass}`}>
        {RESULT_SYMBOL[node.resultClass] ?? node.resultClass}
      </span>

      {node.msgValue !== "0" && (
        <span className="shrink-0 font-evidence text-xs text-ink-muted">
          {formatWei(node.msgValue)}
        </span>
      )}

      {node.relation && <RelationBadge relation={node.relation} />}
    </div>
  );
}
